import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  AlertColor,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Notification from 'components/Notification';
import DrawerMenu from 'components/DrawerMenu';
import PageLoader from 'components/PageLoader';
import TablePaginationAction from 'components/TablePaginationAction';
import dayjs, { Dayjs } from 'dayjs';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import strings from '../../../common/Translation/Translate';
import axiosInstance from '../../../utils/axios';
import { privileges } from '../../../utils/constants/privileges';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { createDropdownData, getDrawerWidth } from '../../../utils/validators/HelperFunctions';
import { pageSubtitleSx, pageTitleSxResponsive } from 'utils/styles/pageHeader';
import DateRangeSelector from './DateRangeSelector';

type SelectOption = {
  label: string;
  value: number;
};

type StringSelectOption = {
  label: string;
  value: string;
};

type BookingRow = {
  id: number;
  bookingReference: string;
  lineItemId: number | null;
  eventDate: string | null;
  customerName: string | null;
  companyName: string | null;
  propertyName: string | null;
  venueNames: string[];
  lineItemCount: number;
  eventTypeId: number | null;
  eventTypeName: string | null;
  eventInParentOrganization: boolean | null;
  sourceId: number | null;
  sourceName: string | null;
  sourceActive: boolean | null;
  revenueOwners: { id: number; name: string }[];
  bookingStatus: string | null;
  bookingValue: number | null;
  createdAt: string | null;
};

type PageResponse = {
  content: BookingRow[];
  totalElements: number;
};

type BulkMode = 'event' | 'source' | 'both';

const EMPTY_PAGE: PageResponse = {
  content: [],
  totalElements: 0,
};

const formatDate = (value?: string | null) => (value ? dayjs(value).format('D MMM YYYY') : '—');

const chipBaseSx = {
  fontWeight: 600,
  fontSize: '0.7rem',
  height: 24,
};

const chipColors = {
  green: { backgroundColor: '#E6F4EA', color: '#188038' },
  amber: { backgroundColor: '#FFF4E5', color: '#D17A00' },
  red: { backgroundColor: '#FDECEA', color: '#D93025' },
  blue: { backgroundColor: '#E8F0FE', color: '#1967D2' },
};

const getStatusChipStyles = (status: string | null) => {
  switch ((status || '').toUpperCase()) {
    case 'CONFIRMED':
      return chipColors.green;
    case 'TENTATIVE':
      return chipColors.amber;
    case 'WAITLISTED':
      return chipColors.blue;
    case 'CANCELLED':
      return { backgroundColor: '#FDECEA', color: '#D93025' };
    default:
      return { backgroundColor: '#F3F4F6', color: '#374151' };
  }
};

const renderCompactVenues = (venueNames: string[]) => {
  if (!venueNames?.length) return '—';
  if (venueNames.length === 1) return venueNames[0];
  return `${venueNames[0]} +${venueNames.length - 1}`;
};

const tableFontSize = '0.8125rem';
const metaFontSize = '0.75rem';
const filterControlSx = {
  flex: '1 1 0',
  minWidth: 160,
  width: '100%',
  '& .MuiInputBase-root': {
    fontSize: '0.75rem',
    minHeight: 32,
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.75rem',
  },
};

const BOOKING_STATUS_FILTER_OPTIONS: StringSelectOption[] = [
  { label: 'Confirmed', value: 'CONFIRMED' },
  { label: 'Tentative', value: 'TENTATIVE' },
  { label: 'Waitlisted', value: 'WAITLISTED' },
  { label: 'Awaiting Confirmation', value: 'AWAITING_CONFIRMATION' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Enquiry', value: 'ENQUIRY' },
];

const compactButtonSx = {
  fontSize: '0.75rem',
  minHeight: 30,
  px: 1.25,
  py: 0.5,
};

const BookingEventSource: FC = () => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));
  const userPrivileges = useSelector(Privilege);
  const canAccess = userPrivileges?.includes(privileges.view_booking_event_source);
  const canEdit = canAccess;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs().endOf('month'));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageResponse, setPageResponse] = useState<PageResponse>(EMPTY_PAGE);
  const [eventOptions, setEventOptions] = useState<SelectOption[]>([]);
  const [sourceOptions, setSourceOptions] = useState<SelectOption[]>([]);
  const [revenueOwnerOptions, setRevenueOwnerOptions] = useState<SelectOption[]>([]);
  const [sourceFilter, setSourceFilter] = useState<SelectOption | null>(null);
  const [bookingStatusFilter, setBookingStatusFilter] = useState<StringSelectOption[]>([]);
  const [revenueOwnerFilter, setRevenueOwnerFilter] = useState<SelectOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [drawerBooking, setDrawerBooking] = useState<BookingRow | null>(null);
  const [drawerDraft, setDrawerDraft] = useState<{
    eventTypeId: number | null;
    sourceId: number | null;
    revenueOwners: SelectOption[];
  } | null>(null);
  const [bulkMode, setBulkMode] = useState<BulkMode>('both');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDraft, setBulkDraft] = useState<{ eventTypeId: number | null; sourceId: number | null }>({
    eventTypeId: null,
    sourceId: null,
  });
  const [bulkRemoveSource, setBulkRemoveSource] = useState(false);
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message ?? 'Request failed',
    });
  };

  const fetchDropdowns = useCallback(async () => {
    try {
      const [{ data: events }, { data: sources }, { data: revenueOwners }] = await Promise.all([
        axiosInstance.get(URLS.getEventTypeList),
        axiosInstance.get(URLS.sourcesList),
        axiosInstance.get(URLS.revenueOwners()),
      ]);

      setEventOptions(createDropdownData(events, ['id', 'name']) || []);
      setSourceOptions(createDropdownData(sources, ['id', 'name']) || []);
      setRevenueOwnerOptions(createDropdownData(revenueOwners, ['id', 'name']) || []);
    } catch (error: any) {
      handleCatch(error);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      });
      if (sourceFilter?.value != null) {
        params.set('agentId', String(sourceFilter.value));
      }
      if (bookingStatusFilter.length > 0) {
        bookingStatusFilter.forEach(status => params.append('bookingStatuses', status.value));
      }
      if (revenueOwnerFilter.length > 0) {
        revenueOwnerFilter.forEach(owner => params.append('revenueOwnerIds', String(owner.value)));
      }
      const { data } = await axiosInstance.post(`${URLS.bookingEventSourcePage}?${params.toString()}`, {
        page: page + 1,
        size: pageSize,
        filter: [],
        sort: [],
      });
      setPageResponse({
        content: (data?.content || []).map((row: BookingRow) => ({
          ...row,
          revenueOwners: row.revenueOwners || [],
        })),
        totalElements: data?.totalElements || 0,
      });
    } catch (error: any) {
      handleCatch(error);
      setPageResponse(EMPTY_PAGE);
    } finally {
      setLoading(false);
    }
  }, [bookingStatusFilter, endDate, page, pageSize, revenueOwnerFilter, sourceFilter, startDate]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const resetTransientState = () => {
    setSelectedIds([]);
    setDrawerBooking(null);
    setDrawerDraft(null);
    setBulkOpen(false);
  };

  const saveBookingUpdates = async (
    bookingIds: number[],
    payload: {
      eventTypeId: number | null;
      sourceId: number | null;
      clearSource?: boolean;
      revenueOwnerIds?: number[] | null;
    },
    successMessage: string,
  ) => {
    try {
      setSaving(true);
      const clearAgent = Boolean(payload.clearSource);
      const updateRevenueOwners = payload.revenueOwnerIds !== undefined && payload.revenueOwnerIds !== null;
      if (payload.eventTypeId == null && payload.sourceId == null && !clearAgent && !updateRevenueOwners) {
        setMessage({
          display: true,
          severity: 'error',
          message: 'Please select an event segment, a source, revenue owners, or choose to remove the source.',
        });
        return;
      }
      await axiosInstance.patch(URLS.updateBookingEventSource, {
        bookingIds,
        eventTypeId: payload.eventTypeId,
        agentId: clearAgent ? null : payload.sourceId,
        clearAgent,
        revenueOwnerIds: updateRevenueOwners ? payload.revenueOwnerIds : null,
      });
      setMessage({ display: true, severity: 'success', message: successMessage });
      resetTransientState();
      await fetchBookings();
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageIds = useMemo(() => pageResponse.content.map(item => item.id), [pageResponse.content]);
  const allSelectedOnPage = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  const getOptionByValue = (options: SelectOption[], value: number | null) =>
    options.find(option => option.value === value) || null;

  const handleToggleAllRows = () => {
    if (allSelectedOnPage) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
      return;
    }
    setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleToggleRow = (bookingId: number) => {
    setSelectedIds(prev => (prev.includes(bookingId) ? prev.filter(id => id !== bookingId) : [...prev, bookingId]));
  };

  const openDrawer = (row: BookingRow) => {
    if (!canEdit) return;
    setDrawerBooking(row);
    setDrawerDraft({
      eventTypeId: row.eventTypeId,
      sourceId: row.sourceId,
      revenueOwners: (row.revenueOwners || []).map(owner => ({
        label: owner.name,
        value: owner.id,
      })),
    });
  };

  const openBulkModal = (mode: BulkMode) => {
    setBulkMode(mode);
    setBulkDraft({ eventTypeId: null, sourceId: null });
    setBulkRemoveSource(false);
    setBulkOpen(true);
  };

  const handleDateRangeChange = (nextStart: Dayjs, nextEnd: Dayjs) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setPage(0);
    resetTransientState();
  };

  const handleResetFilters = () => {
    setStartDate(dayjs().startOf('month'));
    setEndDate(dayjs().endOf('month'));
    setSourceFilter(null);
    setBookingStatusFilter([]);
    setRevenueOwnerFilter([]);
    setPage(0);
    resetTransientState();
  };

  const readOnlyField = (label: string, value: string) => (
    <Grid item xs={12} sm={6} key={label}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || '—'}
      </Typography>
    </Grid>
  );

  const renderNotAssigned = () => (
    <Chip label="Not assigned" size="small" sx={{ ...chipBaseSx, ...chipColors.blue }} />
  );

  const renderEventChip = (row: BookingRow) => {
    if (!row.eventTypeId || !row.eventTypeName) {
      return renderNotAssigned();
    }
    return (
      <Chip
        label={row.eventTypeName}
        size="small"
        sx={{
          ...chipBaseSx,
          ...(row.eventInParentOrganization ? chipColors.green : chipColors.red),
        }}
      />
    );
  };

  const renderSourceChip = (row: BookingRow) => {
    if (!row.sourceId || !row.sourceName) {
      return renderNotAssigned();
    }
    return (
      <Chip
        label={row.sourceName}
        size="small"
        sx={{
          ...chipBaseSx,
          ...(row.sourceActive ? chipColors.green : chipColors.red),
        }}
      />
    );
  };

  const renderRevenueOwners = (row: BookingRow) => {
    const owners = row.revenueOwners || [];
    if (owners.length === 0) {
      return renderNotAssigned();
    }
    const names = owners.map(owner => owner.name).filter(Boolean);
    const label = names.length === 1 ? names[0] : `${names[0]}, +${names.length - 1}`;
    return (
      <Tooltip
        title={
          <Box component="span" sx={{ whiteSpace: 'pre-line' }}>
            {names.join('\n')}
          </Box>
        }>
        <Typography
          component="span"
          sx={{
            fontSize: tableFontSize,
            cursor: 'default',
            display: 'inline-block',
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}>
          {label}
        </Typography>
      </Tooltip>
    );
  };

  if (!canAccess) {
    return <Navigate to={routes.dashboard} replace />;
  }

  return (
    <>
      {(loading || saving) && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Container
        maxWidth={false}
        sx={{
          '&.MuiContainer-root': {
            pl: 0,
            pr: 0,
          },
        }}>
        <Box>
          <Typography sx={pageTitleSxResponsive(screenSizeDownSm)}>
            {strings.menu_text_booking_event_source}
          </Typography>
          <Typography sx={pageSubtitleSx}>
            {strings.booking_event_source_subtitle}
          </Typography>
        </Box>

        <Paper
          sx={{
            mt: 2,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
          }}>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ width: '100%' }}>
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              sx={filterControlSx}
            />
            <Autocomplete
              multiple
              size="small"
              limitTags={1}
              options={BOOKING_STATUS_FILTER_OPTIONS}
              value={bookingStatusFilter}
              onChange={(_, value) => {
                setBookingStatusFilter(value);
                setPage(0);
                resetTransientState();
              }}
              renderInput={params => <TextField {...params} label="Booking Status" size="small" />}
              sx={filterControlSx}
            />
            <Autocomplete
              size="small"
              options={sourceOptions}
              value={sourceFilter}
              onChange={(_, value) => {
                setSourceFilter(value);
                setPage(0);
                resetTransientState();
              }}
              renderInput={params => <TextField {...params} label="Source" size="small" />}
              sx={filterControlSx}
            />
            <Autocomplete
              multiple
              size="small"
              limitTags={1}
              options={revenueOwnerOptions}
              value={revenueOwnerFilter}
              onChange={(_, value) => {
                setRevenueOwnerFilter(value);
                setPage(0);
                resetTransientState();
              }}
              renderInput={params => <TextField {...params} label="Revenue Owners" size="small" />}
              sx={filterControlSx}
            />
            <Tooltip title="Reset filters">
              <IconButton
                size="small"
                aria-label="Reset filters"
                onClick={handleResetFilters}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  height: 32,
                  width: 32,
                  flex: '0 0 auto',
                }}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {selectedIds.length > 0 && canEdit && (
          <Paper
            sx={{
              mt: 2.5,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
            }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }} justifyContent="space-between">
              <Typography sx={{ fontWeight: 600, fontSize: tableFontSize }}>{selectedIds.length} bookings selected</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button size="small" sx={compactButtonSx} variant="outlined" onClick={() => openBulkModal('event')}>
                  Change Event Segment
                </Button>
                <Button size="small" sx={compactButtonSx} variant="outlined" onClick={() => openBulkModal('source')}>
                  Change Source
                </Button>
                <Button size="small" sx={compactButtonSx} variant="contained" onClick={() => openBulkModal('both')}>
                  Change Event Segment & Source
                </Button>
                <Button size="small" sx={compactButtonSx} color="inherit" onClick={() => setSelectedIds([])}>
                  Clear Selection
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {pageResponse.content.length > 0 ? (
          <Paper
            sx={{
              width: '100%',
              overflow: 'hidden',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
              backgroundColor: theme.palette.background.paper,
              mt: 2.5,
            }}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table stickyHeader>
                <TableHead
                  sx={{
                    '.MuiTableCell-root': {
                      backgroundColor: theme.palette.grey[50],
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      fontWeight: 600,
                      fontSize: tableFontSize,
                    },
                  }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={allSelectedOnPage} indeterminate={!allSelectedOnPage && selectedIds.some(id => pageIds.includes(id))} onChange={handleToggleAllRows} />
                    </TableCell>
                    <TableCell>Event Date</TableCell>
                    <TableCell>Customer / Company</TableCell>
                    <TableCell>Venue(s)</TableCell>
                    <TableCell align="right">Line Items</TableCell>
                    <TableCell>Event Segment</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Revenue Owners</TableCell>
                    <TableCell>Booking Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      verticalAlign: 'middle',
                      fontSize: tableFontSize,
                      py: 1,
                    },
                  }}>
                  {pageResponse.content.map(row => {
                    const missingDetails = !row.eventTypeId || !row.sourceId;

                    return (
                      <TableRow key={row.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedIds.includes(row.id)} onChange={() => handleToggleRow(row.id)} />
                        </TableCell>
                        <TableCell>{formatDate(row.eventDate)}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: tableFontSize }}>{row.customerName || '—'}</Typography>
                          <Typography color="text.secondary" sx={{ fontSize: metaFontSize }}>
                            {row.companyName || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.venueNames?.join(', ') || '—'}>
                            <Typography sx={{ fontSize: tableFontSize }}>{renderCompactVenues(row.venueNames || [])}</Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontSize: tableFontSize }}>{row.lineItemCount ?? 0}</Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{renderEventChip(row)}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{renderSourceChip(row)}</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>{renderRevenueOwners(row)}</TableCell>
                        <TableCell>
                          <Chip
                            label={(row.bookingStatus || '—').toLowerCase().replaceAll('_', ' ')}
                            size="small"
                            sx={{
                              ...chipBaseSx,
                              textTransform: 'capitalize',
                              fontWeight: 700,
                              borderRadius: '4px',
                              ...getStatusChipStyles(row.bookingStatus),
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                            <Tooltip title={row.lineItemId ? 'Open booking' : 'No line item available'}>
                              <span>
                                <IconButton
                                  size="small"
                                  component={row.lineItemId ? Link : 'button'}
                                  to={row.lineItemId ? `${routes.bookings}/${row.lineItemId}` : undefined}
                                  disabled={!row.lineItemId}
                                  aria-label="Open booking">
                                  <OpenInNewIcon fontSize="small" color={row.lineItemId ? 'primary' : 'disabled'} />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {missingDetails ? (
                              <Tooltip title="Complete details">
                                <span>
                                  <IconButton size="small" onClick={() => openDrawer(row)} disabled={!canEdit}>
                                    <EditOutlinedIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Edit Event Segment & Source">
                                <span>
                                  <IconButton size="small" onClick={() => openDrawer(row)} disabled={!canEdit}>
                                    <EditOutlinedIcon fontSize="small" color="primary" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Grid
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 1.25, borderTop: `1px solid ${theme.palette.divider}` }}>
              <TablePaginationAction
                count={pageResponse.totalElements}
                rowsPerPage={pageSize}
                page={page}
                onRowsPerPageChange={(event: any) => {
                  setPageSize(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                onPageChange={(_: unknown, nextPage: number) => setPage(nextPage)}
              />
              {!screenSizeDownSm && (
                <Typography sx={{ pr: 2, fontSize: metaFontSize }}>
                  {pageResponse.totalElements === 0
                    ? '0 of 0 records'
                    : `${page * pageSize + 1}-${Math.min((page + 1) * pageSize, pageResponse.totalElements)} of ${pageResponse.totalElements} records`}
                </Typography>
              )}
            </Grid>
          </Paper>
        ) : (
          !loading && (
            <Typography
              sx={{
                p: 2,
                mt: 5,
                borderRadius: 1,
                bgcolor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText,
                fontSize: tableFontSize,
              }}
              variant="h6">
              {strings.no_data_available}
            </Typography>
          )
        )}
      </Container>

      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={Boolean(drawerBooking)}
        handleClose={() => {
          setDrawerBooking(null);
          setDrawerDraft(null);
        }}
        closeButton
        title={drawerBooking && (!drawerBooking.eventTypeId || !drawerBooking.sourceId) ? 'Complete details' : 'Edit Event Segment & Source'}>
        {drawerBooking && drawerDraft && (
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Booking information</Typography>
            <Grid container spacing={2}>
              {readOnlyField('Booking ID', drawerBooking.bookingReference)}
              {readOnlyField('Customer', drawerBooking.customerName || '—')}
              {readOnlyField('Property', drawerBooking.propertyName || '—')}
              {readOnlyField('Event date', formatDate(drawerBooking.eventDate))}
              {readOnlyField('Venue(s)', drawerBooking.venueNames?.join(', ') || '—')}
              {readOnlyField('Booking status', drawerBooking.bookingStatus || '—')}
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography sx={{ fontWeight: 700, mb: 2, fontSize: tableFontSize }}>Editable fields</Typography>
            <Stack spacing={2}>
              <Autocomplete
                options={eventOptions}
                value={getOptionByValue(eventOptions, drawerDraft.eventTypeId)}
                onChange={(_, value) => setDrawerDraft(prev => (prev ? { ...prev, eventTypeId: value?.value ?? null } : prev))}
                renderInput={params => <TextField {...params} label="Event Segment" />}
              />
              <Autocomplete
                options={sourceOptions}
                value={getOptionByValue(sourceOptions, drawerDraft.sourceId)}
                onChange={(_, value) => setDrawerDraft(prev => (prev ? { ...prev, sourceId: value?.value ?? null } : prev))}
                clearOnEscape
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Source"
                    helperText="Clear the field to remove source from this booking"
                  />
                )}
              />
              <Autocomplete
                multiple
                options={revenueOwnerOptions}
                value={drawerDraft.revenueOwners}
                onChange={(_, value) => setDrawerDraft(prev => (prev ? { ...prev, revenueOwners: value } : prev))}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Revenue Owners"
                    helperText="Clear all selections to remove revenue owners"
                  />
                )}
              />
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                variant="contained"
                disabled={!canEdit}
                onClick={() => {
                  const clearSource = drawerDraft.sourceId == null && drawerBooking.sourceId != null;
                  saveBookingUpdates(
                    [drawerBooking.id],
                    {
                      eventTypeId: drawerDraft.eventTypeId,
                      sourceId: drawerDraft.sourceId,
                      clearSource,
                      revenueOwnerIds: drawerDraft.revenueOwners.map(owner => owner.value),
                    },
                    'Booking updated successfully.',
                  );
                }}>
                Save
              </Button>
              <Button
                color="inherit"
                onClick={() => {
                  setDrawerBooking(null);
                  setDrawerDraft(null);
                }}>
                Cancel
              </Button>
            </Stack>
          </Box>
        )}
      </DrawerMenu>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {bulkMode === 'event' ? 'Change Event Segment' : bulkMode === 'source' ? 'Change Source' : 'Change Event Segment & Source'}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2, fontSize: tableFontSize }}>
            This will update the selected value for all {selectedIds.length} bookings. Existing booking line items will not be changed.
          </Typography>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(bulkMode === 'event' || bulkMode === 'both') && (
              <Autocomplete
                options={eventOptions}
                value={getOptionByValue(eventOptions, bulkDraft.eventTypeId)}
                onChange={(_, value) => setBulkDraft(prev => ({ ...prev, eventTypeId: value?.value ?? null }))}
                renderInput={params => <TextField {...params} label="Event Segment" />}
              />
            )}
            {(bulkMode === 'source' || bulkMode === 'both') && (
              <>
                <Autocomplete
                  options={sourceOptions}
                  value={getOptionByValue(sourceOptions, bulkDraft.sourceId)}
                  onChange={(_, value) => {
                    setBulkRemoveSource(false);
                    setBulkDraft(prev => ({ ...prev, sourceId: value?.value ?? null }));
                  }}
                  disabled={bulkRemoveSource}
                  clearOnEscape
                  renderInput={params => <TextField {...params} label="Source" />}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={bulkRemoveSource}
                      onChange={(_, checked) => {
                        setBulkRemoveSource(checked);
                        if (checked) {
                          setBulkDraft(prev => ({ ...prev, sourceId: null }));
                        }
                      }}
                    />
                  }
                  label="Remove source from selected bookings"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !canEdit ||
              (bulkMode === 'event' && bulkDraft.eventTypeId == null) ||
              (bulkMode === 'source' && bulkDraft.sourceId == null && !bulkRemoveSource) ||
              (bulkMode === 'both' &&
                bulkDraft.eventTypeId == null &&
                bulkDraft.sourceId == null &&
                !bulkRemoveSource)
            }
            onClick={() =>
              saveBookingUpdates(
                selectedIds,
                {
                  eventTypeId: bulkMode === 'source' ? null : bulkDraft.eventTypeId,
                  sourceId: bulkMode === 'event' || bulkRemoveSource ? null : bulkDraft.sourceId,
                  clearSource: bulkMode !== 'event' && bulkRemoveSource,
                },
                bulkRemoveSource
                  ? 'Selected bookings updated and source removed.'
                  : 'Selected bookings updated.',
              )
            }>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BookingEventSource;
